import React from 'react'
import { EcommerceResponse, IEcommerceQuestionSchema } from '../../types'
import { getMediumAssetPath } from '@aytm/lib/common/assets'
import { formatPriceWithLocale } from '../../functions'
import { localizeCurrency } from '../functions'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import style from './AddToCartSidebar.module.scss'
import newCustomersTagIcon from '../assets/icons/new-customers-tag.svg'
import autoshipLogo from '../assets/icons/autoship-logo.svg'
import autoshipIcon from '../assets/icons/autoship-icon.svg'
import autoshipSavingsIcon from '../assets/icons/autoship-savings.svg'
import autoshipScheduleIcon from '../assets/icons/autoship-schedule.svg'
import autoshipSkipIcon from '../assets/icons/autoship-skip.svg'
import blueBoom from '../assets/icons/blue-boom.svg'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'

interface IAddToCartSidebarProps {
  schema: IEcommerceQuestionSchema
  response: EcommerceResponse
  addedIndex: number
  totals: number[]
  variant?: 'grid' | 'product'
  checkout: () => void
  onClose: () => void
  onCartOpen: () => void
}

const AUTOSHIP_FIRST_DISCOUNT = 0.5
const AUTOSHIP_FUTURE_DISCOUNT = 0.05

const AddToCartSidebar = ({
  schema,
  response,
  addedIndex,
  totals,
  variant = 'grid',
  checkout,
  onClose,
  onCartOpen
}: IAddToCartSidebarProps) => {
  const { t, languageTag } = useSurveyLocale()
  const viewport = useViewport()
  const answer = schema.answers[addedIndex]

  if (!answer) return null

  const unitPrice = answer.discounted_price || answer.price
  const firstAutoshipPrice = unitPrice * (1 - AUTOSHIP_FIRST_DISCOUNT)
  const futureAutoshipPrice = unitPrice * (1 - AUTOSHIP_FUTURE_DISCOUNT)
  const formatPrice = (price: number) => formatPriceWithLocale(price, schema.price_mask, languageTag, true, false)

  const subtotalOriginal = response.answers.reduce((sum, r, i) => sum + r.v * schema.answers[i].price, 0)
  const hasDiscount = subtotalOriginal > totals[1]
  const valid =
    (schema.max_money_spent ? totals[1] <= schema.max_money_spent : true) &&
    (schema.max_cart_size ? totals[0] <= schema.max_cart_size : true)

  const renderGridBody = () => (
    <>
      <div className={style.productRow}>
        <div className={style.thumbWrap}>
          {answer.image ? (
            <div className={style.thumb} style={{ backgroundImage: `url('${getMediumAssetPath(answer.image)}')` }} />
          ) : (
            <div className={style.thumbNone} />
          )}
        </div>
        <div className={style.productBody}>
          <div className={style.priceLabel}>{t('questions', 'ec', 'chewy', 'gettingItemFor')}</div>
          <div className={style.priceValue} dangerouslySetInnerHTML={{ __html: formatPrice(unitPrice) }} />
          <div className={style.promoLine}>
            <img src={newCustomersTagIcon} alt="" role="presentation" className={style.promoIcon} />
            <span>{localizeCurrency(t('questions', 'ec', 'chewy', 'newCustomersOffer'), schema.price_mask)}</span>
          </div>
        </div>
      </div>

      <h3 className={style.autoshipHeading}>{t('questions', 'ec', 'chewy', 'getItOnRepeat')}</h3>

      <div className={style.autoshipCard}>
        <img src={blueBoom} alt="" role="presentation" className={style.blueBoom} />
        <div className={style.autoshipChoose}>
          <span>{t('questions', 'ec', 'chewy', 'autoshipChoose')}</span>
          <img src={autoshipLogo} alt={t('questions', 'ec', 'chewy', 'autoship')} className={style.autoshipLogo} />
          <span>{t('questions', 'ec', 'chewy', 'autoshipAndPay')}</span>
        </div>
        <div className={style.autoshipFirstRow}>
          <span
            className={style.autoshipFirstPrice}
            dangerouslySetInnerHTML={{ __html: formatPrice(firstAutoshipPrice) }}
          />
          <span className={style.autoshipFirstLabel}>{t('questions', 'ec', 'chewy', 'thisOrder')}</span>
        </div>
        <div className={style.autoshipSavings}>
          <strong>
            {t<(percent: number) => string>('questions', 'ec', 'chewy', 'savePercent')(AUTOSHIP_FIRST_DISCOUNT * 100)}
          </strong>{' '}
          {t('questions', 'ec', 'chewy', 'onFirstAutoshipOrder')}
        </div>
        <div className={style.autoshipFuture}>
          <span dangerouslySetInnerHTML={{ __html: formatPrice(futureAutoshipPrice) }} />{' '}
          {t<(percent: number) => string>('questions', 'ec', 'chewy', 'futureOrders')(AUTOSHIP_FUTURE_DISCOUNT * 100)}
        </div>
        <ul className={style.autoshipBenefits}>
          <li>
            <img src={autoshipSavingsIcon} alt="" role="presentation" className={style.benefitIcon} />
            {t<(percent: number) => string>('questions', 'ec', 'chewy', 'keepSaving')(AUTOSHIP_FUTURE_DISCOUNT * 100)}
          </li>
          <li>
            <img src={autoshipScheduleIcon} alt="" role="presentation" className={style.benefitIcon} />
            {t('questions', 'ec', 'chewy', 'getDeliveries')}
          </li>
          <li>
            <img src={autoshipSkipIcon} alt="" role="presentation" className={style.benefitIcon} />
            {t('questions', 'ec', 'chewy', 'skipChangeCancel')}
          </li>
        </ul>
      </div>

      <div className={style.ctaRow}>
        <button
          type="button"
          className={style.viewCart}
          onClick={() => {
            onClose()
            onCartOpen()
          }}
        >
          {t('questions', 'ec', 'chewy', 'viewCart')}
        </button>
        <button type="button" className={style.switchAutoship}>
          <EmptyLink color="#1C49C4">
            <span>{t('questions', 'ec', 'chewy', 'switchTo')}&nbsp;</span>
            <img src={autoshipIcon} alt="" role="presentation" className={style.switchAutoshipIcon} />
            <span>{t('questions', 'ec', 'chewy', 'autoship')}</span>
          </EmptyLink>
        </button>
      </div>
    </>
  )

  const renderProductBody = () => (
    <>
      <div
        className={style.eGiftBanner}
        dangerouslySetInnerHTML={{
          __html: localizeCurrency(t('questions', 'ec', 'chewy', 'eGiftBanner'), schema.price_mask)
        }}
      />

      <div className={style.productRow}>
        <div className={style.thumbWrap}>
          {answer.image ? (
            <div className={style.thumb} style={{ backgroundImage: `url('${getMediumAssetPath(answer.image)}')` }} />
          ) : (
            <div className={style.thumbNone} />
          )}
        </div>
        <div className={style.productBody}>
          <div className={style.priceLabel}>{t('questions', 'ec', 'chewy', 'gettingItemFor')}</div>
          <div className={style.promoLine}>
            <img src={newCustomersTagIcon} alt="" role="presentation" className={style.promoIcon} />
            <span>{localizeCurrency(t('questions', 'ec', 'chewy', 'newCustomersOffer'), schema.price_mask)}</span>
          </div>
        </div>
      </div>

      <div className={style.shipsRow}>
        <span dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'chewy', 'orderShipsFree') }} />
        <i className={`pi pi-send ${style.truckIcon}`} />
      </div>
      <div className={style.progressBar}>
        <div className={style.progressFill} style={{ width: '100%' }} />
      </div>

      <div className={style.subtotalRow}>
        <span className={style.subtotalLabel}>
          {t<(count: number) => string>('questions', 'ec', 'chewy', 'subtotalItems')(totals[0])}
        </span>
        <span className={style.subtotalValues}>
          <span className={style.subtotalCurrent} dangerouslySetInnerHTML={{ __html: formatPrice(totals[1]) }} />
          {hasDiscount && (
            <span className={style.subtotalOld} dangerouslySetInnerHTML={{ __html: formatPrice(subtotalOriginal) }} />
          )}
        </span>
      </div>

      <div className={style.ctaRow}>
        <button
          type="button"
          className={style.viewCart}
          onClick={() => {
            onClose()
            onCartOpen()
          }}
        >
          {t('questions', 'ec', 'chewy', 'cart')}
        </button>
        <button type="button" className={style.checkout} onClick={checkout} disabled={!valid}>
          {t('questions', 'ec', 'chewy', 'checkout')}
        </button>
      </div>
    </>
  )

  return (
    <div className={`${style.overlay} ${viewport.mobile ? style.mobile : ''}`} onClick={onClose}>
      <aside className={style.sidebar} data-testid="SW-EC-AddToCartSidebar" onClick={e => e.stopPropagation()}>
        <header className={style.header}>
          <div className={style.headerTitle}>
            <i className={`pi pi-check ${style.headerCheck}`} />
            <span>{t('questions', 'ec', 'chewy', 'added')}</span>
          </div>
          <button
            type="button"
            className={style.closeBtn}
            onClick={onClose}
            aria-label={t('questions', 'ec', 'chewy', 'close')}
          >
            <i className="pi pi-times" />
          </button>
        </header>

        {variant === 'grid' ? renderGridBody() : renderProductBody()}
      </aside>
    </div>
  )
}

export default AddToCartSidebar
